import React, { useMemo } from "react";
import { Spin } from "antd";
import { useRefreshAssetPath } from "#src/hooks";
import styled from "styled-components";
import { ITEM_TYPES, ItemType } from "../../types";

export const ImageVideoPreview = React.forwardRef<
  HTMLVideoElement | HTMLImageElement,
  {
    type: Extract<ItemType["type"], "Image" | "Video">;
    assetId?: string;
    path?: string;
    style?: React.CSSProperties;
    videoProps?: React.VideoHTMLAttributes<HTMLVideoElement>;
    imageProps?: React.ImgHTMLAttributes<HTMLImageElement>;
    onClick?: () => void;
    placeholderAssets?: Record<string, string>;
  }
>(
  (
    {
      type,
      assetId,
      path,
      style,
      videoProps,
      imageProps,
      onClick,
      placeholderAssets,
    },
    ref
  ) => {
    const { asset, refreshAssetUrl, currentPath } = useRefreshAssetPath({
      assetId,
      path,
      autoRefresh: true,
      placeholderAssets,
    });

    const assetPath = currentPath;

    const previewContent = useMemo(
      () =>
        type === ITEM_TYPES.Video ? (
          <video
            ref={ref as React.Ref<HTMLVideoElement>}
            src={assetPath}
            draggable="false"
            onError={() => {
              refreshAssetUrl().catch((error) =>
                console.error("Error refreshing video URL:", error)
              );
            }}
            style={{
              objectFit: "cover",
              width: "100%",
              height: "100%",
              ...style,
            }}
            {...videoProps}
          >
            <track kind="captions" src="" />
          </video>
        ) : (
          <img
            ref={ref as React.Ref<HTMLImageElement>}
            src={assetPath}
            draggable="false"
            onError={() => {
              refreshAssetUrl().catch((error) =>
                console.error("Error refreshing image URL:", error)
              );
            }}
            style={{
              objectFit: "cover",
              width: "100%",
              height: "100%",
              ...style,
            }}
            alt={asset?.description ?? "image"}
            {...imageProps}
          />
        ),
      [
        type,
        ref,
        assetPath,
        style,
        videoProps,
        asset?.description,
        imageProps,
        refreshAssetUrl,
      ]
    );

    if (!assetPath)
      return (
        <Spin
          spinning={true}
          size="large"
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        />
      );
    return onClick ? (
      <ImageVideoPreviewWrapper onClick={onClick}>
        {previewContent}
      </ImageVideoPreviewWrapper>
    ) : (
      previewContent
    );
  }
);

ImageVideoPreview.displayName = "ImageVideoPreview";

const ImageVideoPreviewWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
`;
